import { useState, useEffect, useCallback, useRef } from "react";
import Link from "next/link";
import Layout from "../../components/Layout";
import Head from "next/head";
import { CtaBanner } from "./[slug]";
import { BlogIndexSkeleton } from "../../components/Skeleton";
import axiosInstance from "../../config/axios";
import RichTextRenderer from "./RichTextRenderer";
import { axiosInstanceV2 } from "../../config/axios";
import {
  fetchBlogCategories,
  fetchBlogs,
  formatBlogDate,
  shortenExcerpt
} from "../../helpers/blogUtils";

// ── placeholder data — replace with API data later ─────────────────────────
const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "wfn", label: "Win for food" },
  { id: "fc", label: "Food & Culture" },
  { id: "rs", label: "Recipes & Tips" },
  { id: "rn", label: "Restaurant News" },
  { id: "lf", label: "Lifestyle" },
  { id: "up", label: "Updates" },
];

const FEATURED_POST = {
  id: 1,
  category: "Food & Culture",
  title: "The Story Behind Nigeria's Love For Jollof Rice",
  excerpt:
    "From Senegalese kitchens to Lagos parties, jollof rice became the dish that unites Nigerian celebrations. Here is how it happened — and why our taste tells the story.",
  date: "21 Jul 2026",
  author: "Tobi Odeyale",
  image: "/images/blog/jollof-rice.webp",
  slug: "story-behind-nigerias-love-for-jollof-rice",
};

const POSTS = Array.from({ length: 9 }, (_, i) => ({
  id: i + 2,
  category: "Food & Culture",
  title: "What should you buy?",
  excerpt:
    "Discover the vibrant flavors and unique dishes that make Kilimanjaro Restaurant's a culinary destination.",
  date: "21 Jul 2026",
  author: "Tobi Odeyale",
  image: "/images/blog/post-placeholder.webp",
  slug: `what-should-you-buy-${i + 1}`,
}));

// ── sub-components ──────────────────────────────────────────────────────────
function CategoryBadge({ label }) {
  return <span className="blog-category-badge">{label}</span>;
}

function FeaturedCard({ post }) {
  return (
    <section>
      <div className="blog-featured-card">
       <Link href={`/blog/${post.slug}?id=${post.id}`}>
        <div className="row no-gutters" style={{cursor: "pointer"}}>
       
          <div className="col-md-5">
            <div className="blog-featured-card__img-wrap">
              <img
                src={post?.yoast_head_json?.og_image[0]?.url || "/images/kili-staffs.jpg"}
                alt={post.title.rendered}
                className="blog-featured-card__img"
                onError={(e) => {
                  e.target.src =
                    "https://placehold.co/600x400/f5f5f5/ccc?text=Image";
                }}
              />
            </div>
          </div>
          <div className="col-md-7">
            <div className="blog-featured-card__body ">
              <CategoryBadge
  label={post.category_name
    .map((category) => category.replace(/&amp;/g, "&"))
    .join(", ")}
/>
              <h6 className="blog-featured-card__title mt-4">{post.title?.rendered?.replace(/&#038;/g, "&")}</h6>
              {/* <p className="blog-featured-card__excerpt">{post.excerpt}</p> */}
               <RichTextRenderer
                          html={shortenExcerpt(post.excerpt.rendered,180)}
                          className="blog-post-page__content"
                        />
              <p className="blog-featured-card__meta mt-3">
                {formatBlogDate(post.date)} &bull;{" "}
                <span className="blog-featured-card__name">{post.author_name}</span>
              </p>
              
                <a className="blog-btn-primary mt-4">
                  Read Article{" "}
                  <span>
                    <img
                      src="/images/icon/arrow-right-white.svg"
                      alt="arrow right"
                      className="blog-arrow-right-icon"
                    />
                  </span>
                </a>
             
            </div>
          </div>
        </div>
       </Link>
        
      </div>
    </section>
  );
}

export function PostCard({ post }) {
  return (
    <section className="mb-5">
    <Link href={`/blog/${post?.slug}?id=${post?.id}`}>
      <div className="blog-post-card" style={{cursor: "pointer"}}>
      
        <div className="blog-post-card__img-wrap">
          <img
            src={post?.yoast_head_json?.og_image[0]?.url || "/images/kili-staffs.jpg"}
            alt={post?.title.rendered}
            className="blog-post-card__img"
            loading="lazy"
            onError={(e) => {
              e.target.src =
                "https://placehold.co/400x220/f5f5f5/ccc?text=Image";
            }}
          />
        </div>
        <div className="blog-post-card__body ">
          <CategoryBadge
  label={post?.category_name
    .map((category) => category.replace(/&amp;/g, "&"))
    .join(", ")}
/>
          <h6 className="blog-post-card__title mt-4">{post?.title?.rendered?.replace(/&#038;/g, "&")}</h6>
          {/* <p className="blog-post-card__excerpt">{post.excerpt}</p> */}
          <RichTextRenderer
                          html={shortenExcerpt(post?.excerpt.rendered, 180)}
                          className="blog-post-page__content"
                        />
          <p className="blog-post-card__meta mt-3">
            {formatBlogDate(post?.date)} &bull;{" "}
            <span className="blog-post-card__name">{post?.author_name}</span>
          </p>
          
            <a className="blog-read-more mt-1">
              Read More{" "}
              <span>
                <img
                  src="/images/icon/blog-arrow-right.svg"
                  alt="arrow right"
                  className="blog-arrow-right-icon"
                />
              </span>
            </a>
         
        </div>
      </div>
    </Link>
      
    </section>
  );
}

// ── main page ───────────────────────────────────────────────────────────────
export default function BlogIndex() {
  //const [activeCategory, setActiveCategory] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [activeCategory, setActiveCategory] = useState(""); // slug or ""
  const [searchQuery, setSearchQuery] = useState("");
  const [isFiltered, setIsFiltered] = useState(false);
  const [isFeatured, setIsFeatured] = useState(true)

  const searchTimerRef = useRef(null);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchBlogCategories();
        console.log("data blog categories", data)
        setCategories(data ?? []);
      } catch (error) {
        console.error("Failed to fetch blog categories", error);
      }
    };
    loadCategories();
  }, []);

  const filteredPosts = POSTS.filter((post) => {
  const matchesCategory =
    activeCategory === "all" ||
    post.category.id === activeCategory;

  const matchesSearch =
    !searchQuery ||
    post.title.toLowerCase().includes(searchQuery.toLowerCase());

  return matchesCategory && matchesSearch;
});

  const loadBlogs = useCallback(async (activeCategory, searchQuery) => {
    setIsLoading(true);

    try {
      const data = await fetchBlogs({
        categories: activeCategory === "All" ? "" : activeCategory,
        search: searchQuery,
      });

      setBlogs(data ?? []);

      console.log("blogs", data);

      // mark as filtered if any filter is active
      setIsFiltered(!!(activeCategory !== "All" || searchQuery));
    } catch (error) {
      console.error("Failed to fetch blogs", error);
      setBlogs([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // initial load
  useEffect(() => {
    loadBlogs(activeCategory, searchQuery);
  }, [activeCategory, searchQuery]);

 

 
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setIsFeatured(false)
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      loadBlogs(activeCategory, value);
    }, 600);
  };

 
  return (
    <Layout>
      <Head>
        {/* <link rel="canonical" href={canonicalUrl} /> */}
        <title>{blogs[0]?.seo_title}</title>
        <meta
          name="description"
          content={blogs[0]?.seo_description}
        />

        <meta name="keyword" content="MEALS FOOD RESTAURANT NIGERIA" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebSite",
              url: "https://kilimanjaro-restaurants.com",
              name: "Kilimanjaro",
              description:
                "Kilimanjaro is one of the Sundry Foods’ restaurant brands, operating in the Quick Service Restaurant (QSR) segment, the brand has established itself as a market leader and one of the fastest-growing restaurant brands in the country with currently 59 stores across Nigeria and more to come.Kilimanjaro caters to the unique preference of the general populace in this part of sub-Saharan Africa for their local dishes. Our unique menu consists of select popular contemporary and Nigerian offerings. Items on our menu can also be found on dining tables in other African countries. The Kilimanjaro brand was born out of the quest to satisfy both the local and continental food cravings of our people. We deliver fresh, hot, and mouth-watering meals, pastries, sandwiches, and beverages to thousands of customers in the finest environment each day. Our restaurants stand out for their bright colors and lovely ambiance inviting you to dine-in or grab-and-go with the best quality meals.",
              publisher: {
                "@type": "Organization",
                name: "Kilimanjaro",
              },
              contactPoint: {
                "@type": "ContactPoint",
                telephone: "0700 5454 3663",
                contactType: "customer support",
              },
            }),
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "Kilimanjaro",
              url: "https://kilimanjaro-restaurants.com",
              contactPoint: {
                "@type": "ContactPoint",
                telephone: "0700 5454 3663",
                contactType: "customer support",
              },
              address: {
                "@type": "PostalAddress",
                streetAddress: "23 Nzimiro street, Old GRA. Port Harcourt",
                addressLocality: "Port Harcourt",
                addressRegion: "Rivers",
                addressCountry: "Nigeria",
              },
            }),
          }}
        />
      </Head>

      <>
        <section className="blog-index-page">
          {/* ── header ── */}
          <div className="blog-page-header text-center py-5">
            <p className="blog-page-header__label mb-3">
              Kilimanjaro Restaurant Blog
            </p>
            <h4 className="blog-page-header__title">
              Get recipes, food tips, dining ideas and
              <br className="d-none d-md-block" /> stories from Kilimanjaro
              Restaurant.
            </h4>

            {/* search */}
            <div className="blog-search-wrap mx-auto mt-4">
              <div className="blog-search-inner">
                <img
                  src="/images/icon/search-blog.svg"
                  className="blog-search-icon"
                />

                <input
                  type="search"
                  className="blog-search-input"
                  placeholder="Search Blog"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>
            </div>

            {/* category pills */}
            <div className="blog-category-pills mt-3">
              {categories.length > 0 && (
                <button
                  className={`blog-category-pill ${
                    activeCategory === "" ? "blog-category-pill--active" : ""
                  }`}
                  onClick={() => {setActiveCategory(""); setIsFeatured(true) }}
                >
                  All
                </button>
              )}

              {categories.map((cat) => (
                <button
                  key={cat.id}
                  className={`blog-category-pill ${
                    activeCategory === cat.id
                      ? "blog-category-pill--active"
                      : ""
                  }`}
                  onClick={() => {
    setActiveCategory(cat.id);
    setIsFeatured(false)
    
  }}
                >
                   {cat.name.replace(/&amp;/g, "&")}
                </button>
              ))}
            </div>
          </div>
          {isLoading ? (
            <BlogIndexSkeleton />
          ) : (
            <div className="container blog-index-container">
              {/* featured post */}
              { isFeatured && (
  <>
    {blogs.length > 0 && <FeaturedCard post={blogs[0]} />}
  </>
)}

              {blogs.length > 0 ? (
                <>
                  {/* first 6 posts */}
                  <div className="blog-post-grid">
                    {blogs.slice(0, 6).map((blog) => (
                      <PostCard key={blog.id} post={blog} />
                    ))}
                  </div>

                  {/* CTA banner — full width, outside the grid */}
                  {blogs.length > 0 && <CtaBanner />}

                  {/* remaining posts after the banner */}
                  {blogs.length > 6 && (
                    <div className="blog-post-grid mt-4">
                      {blogs.slice(6).map((blog) => (
                        <PostCard key={blog.id} post={blog} />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="blog-empty-state text-center py-5">
                  <p className="text-muted">No blog posts found.</p>
                </div>
              )}
            </div>
          )}
        </section>
      </>
    </Layout>
  );
}
